/**
 * Locale copy the server already wrote into the page (B-0123).
 *
 * For /es/tool/{slug} pages with Spanish copy the server writes title, meta, H1 and FAQ into the HTML and adds
 * `window.__LOCALE_COPY` (server/_core/localeToolHtml.ts). The app renders with createRoot, so without this lookup it
 * would replace that text with English. The text itself lives only on the server: this file holds no copy, English
 * pages never have the global, and nothing is fetched.
 */
export interface ServerLocaleCopy {
  locale: string;
  slug: string;
  title: string;
  description: string;
  h1: string;
  faq: { en: string; q: string; a: string }[];
}

/** The server-provided copy for this tool in this locale, or null (English, no copy, or another tool). */
export function getServerLocaleCopy(toolId: string | undefined, locale: string): ServerLocaleCopy | null {
  if (!toolId || locale === "en" || typeof window === "undefined") return null;
  const c = (window as unknown as { __LOCALE_COPY?: ServerLocaleCopy }).__LOCALE_COPY;
  return c && c.slug === toolId && c.locale === locale ? c : null;
}

/**
 * FAQ with the server's wording applied. `visible` keeps every entry (translated where the server translated it);
 * `schema` is what the server put in FAQPage: only the translated entries, or everything when none matched.
 */
export function applyServerFaq<T extends { question: string; answer: string }>(
  entries: T[] | undefined,
  copy: ServerLocaleCopy | null,
): { visible: T[] | undefined; schema: T[] | undefined } {
  if (!entries || !copy || !copy.faq.length) return { visible: entries, schema: entries };
  const byEn = new Map(copy.faq.map((f) => [f.en, f]));
  const translated: T[] = [];
  const visible = entries.map((e) => {
    const f = byEn.get(e.question);
    if (!f) return e;
    translated.push({ ...e, question: f.q, answer: f.a, source: undefined });
    return { ...e, question: f.q, answer: f.a };
  });
  return { visible, schema: translated.length ? translated : entries };
}
